import PropTypes from 'prop-types';
import RichText from '@activebrands/core-web/libs/storyblok/RichText';
import { useStyletron } from '@activebrands/core-web/libs/styletron';
import responsiveTextStyles from '@activebrands/core-web/utils/responsive-text-styles';
import getPositionStyles from '@activebrands/core-web/utils/style-functions/get-position-styles';
import Heading from 'components/text/Heading';
import Paragraph from 'components/text/Paragraph';

// Decide fontKeys based on the columnLayout (nr of columns) and the blocks context
const fontKeys = {
    fullWidth: {
        heading: ['Primary/40_94_-2_700', null, null, 'Primary/48_94_-2_700', null, 'Primary/56_94_-2_700'],
        subHeading: ['Primary/24_94_-2_700'],
        preamble: ['Misc/20_120_-1_700'],
        paragraph: ['Secondary/16_130_400', null, null, null, null, 'Secondary/14_130_400'],
        quote: {
            text: ['Primary/24_94_-1_700', null, null, 'Primary/40_94_-2_700'],
            author: ['Secondary/14_130_400'],
        },
        testimonial: ['Primary/24_94_-1_700', null, null, 'Primary/40_94_-2_700'],
    },
    oneColumn: {
        heading: ['Primary/40_94_-2_700', null, null, 'Primary/48_94_-2_700', null, 'Primary/56_94_-2_700'],
        subHeading: ['Primary/24_94_-2_700'],
        preamble: ['Misc/20_120_-1_700'],
        paragraph: ['Secondary/16_130_400', null, null, null, null, 'Secondary/14_130_400'],
        quote: {
            text: ['Primary/24_94_-1_700', null, null, 'Primary/40_94_-2_700'],
            author: ['Secondary/14_130_400'],
        },
        testimonial: ['Primary/24_94_-1_700', null, null, 'Primary/40_94_-2_700'],
    },
    twoColumns: {
        heading: ['Primary/40_94_-2_700', null, null, 'Primary/40_94_-2_700', null, 'Primary/48_94_-2_700'],
        subHeading: ['Primary/24_94_-2_700'],
        preamble: ['Misc/20_120_-1_700'],
        paragraph: ['Secondary/16_130_400', null, null, null, null, 'Secondary/14_130_400'],
        quote: {
            text: ['Primary/24_94_-1_700', null, null, 'Primary/40_94_-2_700'],
            author: ['Secondary/14_130_400'],
        },
        testimonial: ['Primary/24_94_-1_700', null, null, 'Primary/40_94_-2_700'],
    },
    threeColumns: {
        heading: ['Primary/40_94_-2_700', null, null, 'Primary/40_94_-2_700', null, 'Primary/48_94_-2_700'],
        subHeading: ['Primary/24_94_-2_700'],
        preamble: ['Misc/20_120_-1_700'],
        paragraph: ['Secondary/16_130_400'],
        quote: {
            text: ['Primary/24_94_-1_700', null, null, 'Primary/40_94_-2_700'],
            author: ['Secondary/14_130_400'],
        },
        testimonial: ['Primary/24_94_-1_700', null, null, 'Primary/40_94_-2_700'],
    },
    fourColumns: {
        heading: ['Primary/40_94_-2_700', null, null, 'Primary/40_94_-2_700', null, 'Primary/48_94_-2_700'],
        subHeading: ['Primary/24_94_-2_700'],
        preamble: ['Misc/20_120_-1_700'],
        paragraph: ['Secondary/16_130_400'],
        quote: {
            text: ['Primary/24_94_-1_700', null, null, 'Primary/40_94_-2_700'],
            author: ['Secondary/14_130_400'],
        },
        testimonial: ['Primary/24_94_-1_700', null, null, 'Primary/40_94_-2_700'],
    },
};

// Decide Text Component Styling based on the columnLayout (nr of columns)
const textComponentStyling = {
    fullWidth: {
        heading: { marginBottom: ['32px', null, null, '48px'] },
        paragraph: { marginBottom: ['16px', null, null, '24px'] },
        preamble: { marginBottom: ['32px', null, null, '24px'] },
        subHeading: { marginBottom: ['16px', null, null, '24px'] },
        testimonial: { textAlign: 'center', padding: '40px 0' },
        quote: { textAlign: 'center', padding: '40px 0' },
    },
    oneColumn: {
        heading: { marginBottom: ['32px', null, null, '48px'] },
        paragraph: { marginBottom: ['16px', null, null, '24px'] },
        preamble: { marginBottom: ['32px', null, null, '24px'] },
        subHeading: { marginBottom: ['16px', null, null, '24px'] },
        testimonial: { textAlign: 'center', padding: '40px 0' },
        quote: { textAlign: 'center', padding: '40px 0' },
    },
    twoColumns: {
        heading: { marginBottom: ['32px', null, null, '48px'] },
        paragraph: { marginBottom: ['16px', null, null, '24px'] },
        preamble: { marginBottom: ['32px', null, null, '24px'] },
        subHeading: { marginBottom: ['16px', null, null, '24px'] },
        testimonial: { textAlign: 'center', padding: '40px 0' },
        quote: { textAlign: 'center', padding: '40px 0' },
    },
    threeColumns: {
        heading: { marginBottom: ['32px', null, null, '48px'] },
        paragraph: { marginBottom: ['16px', null, null, '24px'] },
        preamble: { marginBottom: ['32px', null, null, '24px'] },
        subHeading: { marginBottom: ['16px', null, null, '24px'] },
        testimonial: { textAlign: 'center', padding: '40px 0' },
        quote: { textAlign: 'center', padding: '40px 0' },
    },
    fourColumns: {
        heading: { marginBottom: ['32px', null, null, '48px'] },
        paragraph: { marginBottom: ['16px', null, null, '24px'] },
        preamble: { marginBottom: ['32px', null, null, '24px'] },
        subHeading: { marginBottom: ['16px', null, null, '24px'] },
        testimonial: { textAlign: 'center', padding: '40px 0' },
        quote: { textAlign: 'center', padding: '40px 0' },
    },
};

// Decide Content Wrapper Styling based on the columnLayout (nr of columns)
const contentWrapperStyles = {
    fullWidth: { width: '100%' }, // special case for accordion
    oneColumn: { width: ['100%', null, null, null, null, '50%'] },
    twoColumns: { width: '100%', padding: ['var(--margin-content)', null, null, '0 40px'] },
    threeColumns: { width: '100%', padding: ['var(--margin-content)', null, null, '0 40px'] },
    fourColumns: { width: '100%', padding: ['var(--margin-content)', null, null, '0 40px'] },
};

// Custom Styles for using Text Component HeadingStyled block and its size alternatives
const styledHeadingFontKeys = {
    sm: ['Primary/20_94_-1_700', null, null, 'Primary/24_94_-1_700', null, 'Primary/32_94_-2_700'],
    md: ['Primary/40_94_-2_700', null, null, 'Primary/48_94_-2_700', null, 'Primary/56_94_-2_700'],
    lg: ['Primary/48_94_-2_700', null, null, 'Primary/56_94_-2_700', null, 'Primary/80_94_-1_700'],
};

const Text = ({ contentHorizontalAlignment = 'left', id = '', items = [], layout = 'oneColumn' }) => {
    const [css] = useStyletron();

    if (!items?.length) {
        return null;
    }

    const textComponentFontKeys = fontKeys[layout];

    const positionStyles = getPositionStyles({
        contentHorizontalAlignment,
        contentVerticalAlignment: 'top',
    });

    return (
        <div className={css({ display: 'flex', ...positionStyles })} id={id}>
            <div className={css({ ...contentWrapperStyles[layout] })}>
                {items.map(item => {
                    switch (item.component) {
                        case 'headingStyled':
                        case 'heading':
                            const fontKeys =
                                item.style === 'default'
                                    ? textComponentFontKeys.heading
                                    : styledHeadingFontKeys[item.style];
                            return (
                                <Heading
                                    $style={textComponentStyling[layout]['heading']}
                                    key={item._uid}
                                    as={item.type || "h2"}
                                    fontKeys={fontKeys}
                                    {...item.sbEditable}
                                >
                                    {item.text}
                                </Heading>
                            );
                        case 'paragraph':
                            return (
                                <div
                                    key={item._uid}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.paragraph),
                                        ...textComponentStyling[layout]['paragraph'],
                                    })}
                                    {...item.sbEditable}
                                >
                                    <RichText data={item.text} />
                                </div>
                            );
                        case 'preamble':
                            return (
                                <div
                                    key={item._uid}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.preamble),
                                        ...textComponentStyling[layout]['preamble'],
                                    })}
                                    {...item.sbEditable}
                                >
                                    <RichText data={item.text} />
                                </div>
                            );
                        case 'subHeading':
                            return (
                                <Heading
                                    key={item._uid}
                                    $style={textComponentStyling[layout]['subHeading']}
                                    as={item.type || "h3"}
                                    fontKeys={textComponentFontKeys.subHeading}
                                    {...item.sbEditable}
                                >
                                    {item.text}
                                </Heading>
                            );
                        case 'testimonial':
                            return (
                                <div
                                    key={item._uid}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.testimonial),
                                        ...textComponentStyling[layout]['testimonial'],
                                    })}
                                    {...item.sbEditable}
                                >
                                    <RichText data={item.text} />
                                </div>
                            );
                        case 'quote':
                            return (
                                <div
                                    key={item._uid}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.quote.text),
                                        ...textComponentStyling[layout]['quote'],
                                    })}
                                    {...item.sbEditable}
                                >
                                    <RichText data={item.text} />
                                    <Paragraph
                                        $style={{ marginTop: '24px' }}
                                        fontKeys={textComponentFontKeys.quote.author}
                                    >
                                        — {item.author}
                                    </Paragraph>
                                </div>
                            );

                        default:
                            return null;
                    }
                })}
            </div>
        </div>
    );
};

Text.propTypes = {
    contentHorizontalAlignment: PropTypes.string,
    id: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.object),
    layout: PropTypes.oneOf(['oneColumn', 'twoColumns', 'threeColumns', 'fourColumns']),
};

export default Text;
